/**
 * Runtime public config: values that would normally be NEXT_PUBLIC_* but are
 * provided at runtime (e.g. from container env) instead of build time.
 * The server injects window.__PUBLIC_CONFIG__ in the root layout from process.env;
 * client code reads it here so we don't need build-time NEXT_PUBLIC_* in Docker.
 */

export interface RuntimePublicConfig {
  NEXT_PUBLIC_APP_URL: string;
  NEXT_PUBLIC_ENABLE_ERROR_TEST_PAGE: string;
  NEXT_PUBLIC_POSTHOG_HOST: string;
  NEXT_PUBLIC_POSTHOG_KEY: string;
  NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN: string;
  NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: string;
}

const DEFAULTS: RuntimePublicConfig = {
  NEXT_PUBLIC_APP_URL: "https://needbridge.com",
  NEXT_PUBLIC_ENABLE_ERROR_TEST_PAGE: "",
  NEXT_PUBLIC_POSTHOG_HOST: "",
  NEXT_PUBLIC_POSTHOG_KEY: "",
  NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN: "",
  NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: "",
};

function fromProcessEnv(): RuntimePublicConfig {
  return Object.fromEntries(
    (Object.keys(DEFAULTS) as (keyof RuntimePublicConfig)[]).map((key) => [
      key,
      process.env[key] ?? DEFAULTS[key],
    ]),
  ) as unknown as RuntimePublicConfig;
}

declare global {
  interface Window {
    __PUBLIC_CONFIG__?: RuntimePublicConfig;
  }
}

/**
 * Get public config. On the server uses process.env (runtime); in the browser
 * uses window.__PUBLIC_CONFIG__ (injected by root layout from server env).
 * Call this instead of reading process.env.NEXT_PUBLIC_* in shared/client code.
 */
export function getRuntimePublicConfig(): RuntimePublicConfig {
  if (typeof window === "undefined") {
    return fromProcessEnv();
  }

  const injected = window.__PUBLIC_CONFIG__;

  if (injected && typeof injected === "object") {
    return { ...DEFAULTS, ...injected };
  }

  return fromProcessEnv();
}

/**
 * Script body to inject in root layout so the client has runtime config.
 * Server-only; pass the result of getRuntimePublicConfig() from process.env.
 */
export function getPublicConfigScript(config: RuntimePublicConfig): string {
  return `window.__PUBLIC_CONFIG__=${JSON.stringify(config)};`;
}
